import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { History, Pencil, Plus, RefreshCcw, Search, WalletCards, Eye, TrendingUp } from "lucide-react";
import { getAllUsersRequest } from "../../../redux/actions/userActions";
import { payrollFetchResourceRequest, payrollSaveResourceRequest } from "../../../redux/actions/payrollActions";
import "./HistoriqueSalairesPage.css";
import "../../adminrh/donnesrh/Trombinoscope.css";

const today = () => new Date().toISOString().slice(0, 10);

const userName = (user) =>
    user?.fullName ||
    `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
    user?.username ||
    `#${user?.id}`;

const getInitials = (user) => {
    const n = userName(user);
    const parts = n.split(" ").filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return n.slice(0, 2).toUpperCase();
};

const MOTIF_META = {
    EMBAUCHE:          { cls: "hsp-badge-embauche",     label: "Embauche" },
    AUGMENTATION:      { cls: "hsp-badge-augmentation", label: "Augmentation" },
    PROMOTION:         { cls: "hsp-badge-promotion",    label: "Promotion" },
    AJUSTEMENT_ANNUEL: { cls: "hsp-badge-ajustement",   label: "Ajustement annuel" },
    AUTRE:             { cls: "hsp-badge-autre",         label: "Autre" },
};

const MotifBadge = ({ motif }) => {
    const meta = MOTIF_META[motif] || MOTIF_META.AUTRE;
    return <span className={`hsp-badge ${meta.cls}`}>{motif}</span>;
};

const HistoriqueSalairesPage = () => {
    const dispatch = useDispatch();
    const { historiqueSalaires = [], loading, saving } = useSelector((s) => s.payroll || {});
    const users = useSelector((s) => s.user?.users || []);

    const [q, setQ] = useState("");
    const [formModal, setFormModal] = useState({ open: false, editing: null });
    const [historyModalUser, setHistoryModalUser] = useState(null);
    const [form, setForm] = useState({
        userId: "",
        salaireBase: "",
        dateDebut: today(),
        motif: "EMBAUCHE",
        commentaire: "",
    });

    useEffect(() => {
        dispatch(getAllUsersRequest());
        dispatch(payrollFetchResourceRequest("historiqueSalaires"));
    }, [dispatch]);

    const userRows = useMemo(() => {
        const needle = q.trim().toLowerCase();
        const filteredUsers = needle
            ? users.filter((u) => userName(u).toLowerCase().includes(needle))
            : users;

        return filteredUsers.map((user) => {
            const history = historiqueSalaires
                .filter((h) => h.userId === user.id)
                .sort((a, b) => new Date(b.dateDebut) - new Date(a.dateDebut));
            return { user, current: history[0] || null, history };
        });
    }, [users, historiqueSalaires, q]);

    const openFormModal = (editingRow = null, defaultUserId = "") => {
        setForm(
            editingRow
                ? {
                    userId: editingRow.userId || "",
                    salaireBase: editingRow.salaireBase || "",
                    dateDebut: editingRow.dateDebut || today(),
                    motif: editingRow.motif || "EMBAUCHE",
                    commentaire: editingRow.commentaire || "",
                }
                : { userId: defaultUserId, salaireBase: "", dateDebut: today(), motif: "EMBAUCHE", commentaire: "" }
        );
        setFormModal({ open: true, editing: editingRow });
    };

    const closeFormModal = () => setFormModal({ open: false, editing: null });

    const save = () => {
        if (!form.userId || !form.salaireBase || !form.dateDebut || !form.motif) {
            Swal.fire({ icon: "warning", text: "Collaborateur, salaire, date d'effet et motif sont obligatoires." });
            return;
        }
        const payload = {
            userId: Number(form.userId),
            salaireBase: Number(form.salaireBase),
            dateDebut: form.dateDebut,
            motif: form.motif,
            commentaire: form.commentaire || null,
        };
        if (formModal.editing?.dateFin) payload.dateFin = formModal.editing.dateFin;
        dispatch(payrollSaveResourceRequest("historiqueSalaires", payload, formModal.editing?.id || null));
        closeFormModal();
    };

    return (
        <div className="hsp-page">

            {/* ── HEADER ── */}
            <div className="hsp-header">
                <div>
                    <h1><WalletCards size={20} />Historique des salaires</h1>
                    <p>Évolution des salaires par collaborateur.</p>
                </div>
                <div className="hsp-header-actions">
                    <button
                        className="hsp-btn hsp-btn-outline"
                        type="button"
                        onClick={() => dispatch(payrollFetchResourceRequest("historiqueSalaires"))}
                    >
                        <RefreshCcw size={14} /> Actualiser
                    </button>
                    <button
                        className="hsp-btn hsp-btn-primary"
                        style={{ backgroundColor: "#dc3545", borderColor: "#dc3545" }}
                        type="button"
                        onClick={() => openFormModal(null)}
                    >
                        <TrendingUp size={14} /> Révision du salaire
                    </button>
                </div>
            </div>

            {/* ── TOOLBAR ── */}
            <div className="hsp-toolbar">
                <div className="hsp-search">
                    <Search size={14} />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Rechercher un collaborateur..."
                    />
                </div>
                <div className="hsp-meta">
                    {loading ? "Chargement..." : `${userRows.length} collaborateur(s)`}
                </div>
            </div>

            {/* ── TABLE ── */}
            <div className="hsp-table-card">
                <table className="hsp-table">
                    <thead>
                    <tr>
                        <th>Collaborateur</th>
                        <th>Salaire actuel</th>
                        <th>Date d'effet</th>
                        <th>Motif</th>
                        <th style={{ textAlign: "center" }}>Historique</th>
                    </tr>
                    </thead>
                    <tbody>
                    {userRows.length === 0 ? (
                        <tr>
                            <td colSpan="5" className="hsp-empty">Aucun collaborateur trouvé</td>
                        </tr>
                    ) : (
                        userRows.map((row) => (
                            <tr key={row.user.id}>
                                <td className="hsp-collab-name">{userName(row.user)}</td>
                                <td>
                                    {row.current
                                        ? <strong>{Number(row.current.salaireBase).toLocaleString("fr-FR")} MAD</strong>
                                        : <span style={{ color: "#9ca3af" }}>Non défini</span>}
                                </td>
                                <td style={{ color: "#6b7280" }}>
                                    {row.current ? row.current.dateDebut : "—"}
                                </td>
                                <td>
                                    {row.current ? <MotifBadge motif={row.current.motif} /> : "—"}
                                </td>
                                <td style={{ textAlign: "center" }}>
                                    <button
                                        type="button"
                                        title="Voir l'historique"
                                        className={`hsp-eye-btn ${row.history.length > 0 ? "has-hist" : "no-hist"}`}
                                        onClick={() => row.history.length > 0 && setHistoryModalUser(row)}
                                    >
                                        <Eye size={15} />
                                        {row.history.length > 0 && (
                                            <span>{row.history.length}</span>
                                        )}
                                    </button>
                                </td>
                            </tr>
                        ))
                    )}
                    </tbody>
                </table>
            </div>

            {/* ══════════════════════════════════════════
                MODAL — HISTORIQUE TIMELINE
            ══════════════════════════════════════════ */}
            {historyModalUser && (
                <div className="hsp-overlay" onClick={() => setHistoryModalUser(null)}>
                    <div
                        className="hsp-modal"
                        style={{ maxWidth: "580px" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header modal */}
                        <div className="hsp-modal-header">
                            <h3><History size={16} />Historique des salaires</h3>
                            <button className="hsp-modal-close" onClick={() => setHistoryModalUser(null)}>✕</button>
                        </div>

                        {/* Body */}
                        <div className="hsp-modal-body">
                            {/* Profil */}
                            <div className="hsp-hist-profile">
                                <div className="hsp-hist-avatar">
                                    {getInitials(historyModalUser.user)}
                                </div>
                                <div>
                                    <p className="hsp-hist-name">{userName(historyModalUser.user)}</p>
                                    <p className="hsp-hist-sub">
                                        {historyModalUser.user.job?.title ||
                                            historyModalUser.user.typeContrat ||
                                            "Collaborateur"}
                                    </p>
                                </div>
                                <div className="hsp-hist-actions">
                                    <button
                                        className="hsp-btn hsp-btn-primary hsp-btn-sm"
                                        onClick={() => openFormModal(null, historyModalUser.user.id)}
                                    >
                                        <Plus size={13} /> Nouvelle révision
                                    </button>
                                </div>
                            </div>

                            {/* Timeline */}
                            <div className="hsp-section-title">
                                <TrendingUp size={13} /> Historique des salaires
                            </div>

                            {historyModalUser.history.length === 0 ? (
                                <div className="hsp-empty-timeline">Aucun historique disponible.</div>
                            ) : (
                                <div className="hsp-timeline">
                                    {historyModalUser.history.map((h, idx) => {
                                        const isCurrent = h.dateFin == null;
                                        return (
                                            <div className="hsp-timeline-item" key={h.id || idx}>
                                                <div className="hsp-timeline-dot-wrap">
                                                    <div className={`hsp-timeline-dot ${isCurrent ? "is-current" : ""}`} />
                                                    {idx < historyModalUser.history.length - 1 && (
                                                        <div className="hsp-timeline-line" />
                                                    )}
                                                </div>
                                                <div className={`hsp-timeline-card ${isCurrent ? "is-current" : ""}`}>
                                                    <div className="hsp-timeline-salary">
                                                        {Number(h.salaireBase).toLocaleString("fr-FR")} MAD
                                                        {isCurrent && (
                                                            <span className="hsp-timeline-current-badge">Actuel</span>
                                                        )}
                                                    </div>
                                                    <div className="hsp-timeline-motif">
                                                        Motif : <strong>{h.motif}</strong>
                                                    </div>
                                                    <div className="hsp-timeline-dates">
                                                        Du {h.dateDebut || "—"}{" "}
                                                        {h.dateFin ? `au ${h.dateFin}` : "à aujourd'hui"}
                                                    </div>
                                                    {h.commentaire && (
                                                        <div className="hsp-timeline-note">
                                                            Note : {h.commentaire}
                                                        </div>
                                                    )}
                                                    <button
                                                        type="button"
                                                        title="Modifier"
                                                        className="hsp-timeline-edit-btn"
                                                        onClick={() => openFormModal(h)}
                                                    >
                                                        <Pencil size={13} />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ══════════════════════════════════════════
                MODAL — FORMULAIRE RÉVISION
            ══════════════════════════════════════════ */}
            {formModal.open && (
                <div className="hsp-overlay" onClick={closeFormModal} style={{ zIndex: 1100 }}>
                    <div
                        className="hsp-modal"
                        style={{ maxWidth: "480px" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="hsp-modal-header">
                            <h3>
                                <TrendingUp size={16} />
                                {formModal.editing ? "Modifier le salaire" : "Révision du salaire"}
                            </h3>
                            <button className="hsp-modal-close" onClick={closeFormModal}>✕</button>
                        </div>

                        <div className="hsp-modal-body">
                            <div className="hsp-form-grid">

                                <div className="hsp-field">
                                    <label>Collaborateur *</label>
                                    <select
                                        value={form.userId}
                                        onChange={(e) => setForm({ ...form, userId: e.target.value })}
                                        disabled={!!historyModalUser || !!formModal.editing}
                                    >
                                        <option value="">Sélectionner</option>
                                        {users.map((u) => (
                                            <option key={u.id} value={u.id}>{userName(u)}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="hsp-field">
                                    <label>Salaire (MAD) *</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        placeholder="Ex : 12 000"
                                        value={form.salaireBase}
                                        onChange={(e) => setForm({ ...form, salaireBase: e.target.value })}
                                    />
                                </div>

                                <div className="hsp-field">
                                    <label>Motif *</label>
                                    <select
                                        value={form.motif}
                                        onChange={(e) => setForm({ ...form, motif: e.target.value })}
                                    >
                                        <option value="EMBAUCHE">EMBAUCHE</option>
                                        <option value="AUGMENTATION">AUGMENTATION</option>
                                        <option value="PROMOTION">PROMOTION</option>
                                        <option value="AJUSTEMENT_ANNUEL">AJUSTEMENT_ANNUEL</option>
                                        <option value="AUTRE">AUTRE</option>
                                    </select>
                                </div>

                                <div className="hsp-field">
                                    <label>Date d'effet *</label>
                                    <input
                                        type="date"
                                        value={form.dateDebut}
                                        onChange={(e) => setForm({ ...form, dateDebut: e.target.value })}
                                    />
                                    {form.dateDebut && form.dateDebut < today() && (
                                        <span className="hsp-field-hint">
                                            ⚠️ La révision s'appliquera avec un effet rétroactif
                                        </span>
                                    )}
                                </div>

                                <div className="hsp-field">
                                    <label>Commentaire</label>
                                    <input
                                        type="text"
                                        value={form.commentaire}
                                        onChange={(e) => setForm({ ...form, commentaire: e.target.value })}
                                        placeholder="Optionnel..."
                                    />
                                </div>

                            </div>
                        </div>

                        <div className="hsp-modal-footer">
                            <button className="hsp-btn hsp-btn-outline" type="button" onClick={closeFormModal}>
                                Annuler
                            </button>
                            <button
                                className="hsp-btn hsp-btn-primary"
                                type="button"
                                disabled={saving}
                                onClick={save}
                            >
                                Enregistrer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HistoriqueSalairesPage;